/* NPM */
import React from 'react';
/* COMPONENTS */
import { Flex, MDIIcon } from '@solinkcorp/solink-components';
import HorizontalCameraEditor from '../../HorizontalCameraEditor/HorizontalCameraEditor';
import CameraAddons from './CameraAddons/CameraAddons';
/* MISC */
import MapMarker from '@mdi/svg/svg/map-marker.svg?react';
import { type IEvent } from 'clients/PlatformApi';
import useLocale from 'src/hooks/useLocale';
import { type EventContextType, type PushToCloudStatus } from 'vd-constants';
import { type GetPushToCloudStatusForOneCam } from '../SavePanel';
import { type CamerasVideoSourceMap, type CameraVideoSource, type SavePanelCamera } from '../types';

interface SelectedCamerasProps {
  availableCameras: SavePanelCamera[];
  cameras: { [key: string]: SavePanelCamera };
  canDownload: boolean;
  canEdit: boolean;
  camerasVideoSourceMap: CamerasVideoSourceMap;
  cloudStatus?: PushToCloudStatus;
  contextMode: EventContextType;
  cameraSelectorRef?: React.RefObject<HTMLDivElement>;
  debugMode?: boolean;
  event?: IEvent;
  generateProgressName: (eventId: string, streamId: string | null) => string;
  getDeviceName?: (deviceId: string) => string | undefined;
  isLoading: boolean;
  isCameraSelectorOpen: boolean;
  isCheckingVideo: boolean;
  locationName: string;
  originalCameras: { [key: string]: SavePanelCamera };
  saveInProgress: boolean;
  saveMode: boolean;
  updateCameraSelectorOpen: (isSelectorOpen: boolean) => void;
  updateCamSelection: (cameras: { [key: string]: SavePanelCamera }) => void;
  requestVmsPushForOneCam: (camera: CameraVideoSource) => Promise<unknown>;
  getPushToCloudStatusForOneCam: GetPushToCloudStatusForOneCam;
  startTime: Date | undefined;
  endTime: Date | undefined;
}

const SelectedCameras: React.FC<SelectedCamerasProps> = (props) => {
  const {
    availableCameras,
    cameras,
    cameraSelectorRef,
    camerasVideoSourceMap,
    canDownload = true,
    canEdit = true,
    cloudStatus,
    contextMode,
    debugMode,
    event,
    getDeviceName,
    isCameraSelectorOpen,
    isCheckingVideo,
    isLoading,
    locationName,
    originalCameras,
    requestVmsPushForOneCam,
    saveInProgress,
    saveMode,
    updateCameraSelectorOpen,
    updateCamSelection,
    getPushToCloudStatusForOneCam,
    startTime,
    endTime,
  } = props;
  const locale = useLocale();

  const cameraAddonsProps = {
    locale,
    canDownload,
    cloudStatus,
    contextMode,
    isLoading,
    isCameraSelectorOpen,
    saveInProgress,
    saveMode,
  };

  const renderCameraAddons = (camera: SavePanelCamera) => {
    if (!camera) return null;
    const clipVideoCamera = camerasVideoSourceMap[camera.id];
    if (!clipVideoCamera) return null;
    return (
      <CameraAddons
        camera={camera}
        clipVideoCamera={clipVideoCamera}
        pushVideo={requestVmsPushForOneCam}
        getPushToCloudStatus={getPushToCloudStatusForOneCam}
        eventId={event?.id}
        eventTitle={event?.title}
        startTime={startTime}
        endTime={endTime}
        {...cameraAddonsProps}
      />
    );
  };

  const horizontalCameraEditorTitle = (
    <Flex className="title" alignItems="center">
      <MDIIcon icon={MapMarker} size={16} />
      <div>{`${locale.cloudShare.location}:  ${locationName}`}</div>
    </Flex>
  );

  return (
    <div className="cameras">
      <HorizontalCameraEditor
        canAdd={saveMode}
        canEdit={canEdit}
        cameraSelectorRef={cameraSelectorRef}
        selectedCameras={cameras}
        renderCameraAddons={renderCameraAddons}
        availableCameras={availableCameras}
        originalCameras={originalCameras}
        isCameraSelectorOpen={isCameraSelectorOpen}
        isLoading={isCheckingVideo}
        title={horizontalCameraEditorTitle}
        updateCameraSelectorOpen={updateCameraSelectorOpen}
        updateCamSelection={updateCamSelection}
        debugMode={debugMode}
        getDeviceName={debugMode ? getDeviceName : undefined}
        hideOverflow={true}
      />
    </div>
  );
};

export default React.memo(SelectedCameras);
